import clsx from "clsx";
import { graphql, Link } from "gatsby";
import { GatsbyImage } from "gatsby-plugin-image";
import React, { FC } from "react";

import { useFormattedDate } from "../../../hooks/use-formatted-date";
import { usePageFullPath } from "../../../hooks/use-page-full-path";
import { CommonColors } from "../../../types/colors";
import { WhatsNextCategory } from "../../whats-next-category";

export interface WhatsNextCardProps extends Queries.WhatsNextDataFragment {
  showBody?: boolean;
  colorScheme?: CommonColors.Dark | CommonColors.Light;
}

export const WhatsNextCard: FC<WhatsNextCardProps> = ({
  title,
  categories,
  image,
  date,
  standardpage,
  showBody = true,
  colorScheme = CommonColors.Dark,
}) => {
  const pageId = standardpage?.[0]?.id;
  const pageUrl = usePageFullPath(pageId);
  const textClassName = colorScheme === CommonColors.Light ? "text-light" : "text-dark";
  const borderClassName = colorScheme === CommonColors.Light ? "border-light" : "border-dark";

  const formattedDate = useFormattedDate(date, {
    year: "numeric",
    month: "2-digit",
    day: "numeric",
  });

  return (
    <Link className="flex flex-col gap-3" to={showBody ? pageUrl || "" : ""}>
      <div className="rounded-4xl overflow-hidden">
        {image?.gatsbyImageData && (
          <GatsbyImage
            image={image?.gatsbyImageData}
            alt={title ?? ""}
            objectFit="cover"
            className="w-full h-full aspect-square"
          />
        )}
      </div>
      {showBody ? (
        <>
          <div className="flex justify-center gap-3 pt-1">
            {categories?.map(
              (category, index) =>
                category && (
                  <WhatsNextCategory
                    className={clsx(textClassName, borderClassName)}
                    key={index}
                    {...category}
                  />
                )
            )}
          </div>
          <div
            className={clsx("flex-1 text-xl 2xl:text-2xl font-medium text-center", textClassName)}
          >
            {title}
          </div>
          <div className={clsx("uppercase font-light text-lg text-center", textClassName)}>
            {formattedDate}
          </div>
        </>
      ) : null}
    </Link>
  );
};

export const query = graphql`
  fragment WhatsNextData on ContentfulWhatsNext {
    node_locale
    id
    title
    date
    categories {
      ...WhatsNextCategoryData
    }
    image {
      gatsbyImageData
      mimeType
    }
    standardpage {
      id
    }
  }
`;
